// API service for Preview Studio

import {
  API_AD_PREVIEWS_SIMULATE_URL,
  API_PREVIEW_CONTEXT_URL,
  API_PREVIEW_GALLERIES_URL,
  API_PREVIEW_GALLERY_SHOW_URL,
} from '../../constants/apiUrls';
import {
  CreateGalleryRequest,
  DeviceOS,
  DeviceType,
  GalleryResponse,
  LivePreviewEligibility,
  PreviewContext,
  SimulatePreviewRequest,
  SimulatePreviewResponse,
} from '../../types/previewStudio';
import { getRequestJSON, postRequestJSON } from '../../util/services';

/**
 * Fetch preview context for a campaign or creative
 */
export async function fetchPreviewContext(
  apiHost: string,
  params: {
    campaign_id?: number;
    creative_id?: number;
  }
): Promise<PreviewContext> {
  const searchParams = new URLSearchParams();
  if (params.campaign_id) {
    searchParams.append('campaign_id', params.campaign_id.toString());
  }
  if (params.creative_id) {
    searchParams.append('creative_id', params.creative_id.toString());
  }

  const response = await getRequestJSON(
    apiHost,
    `${API_PREVIEW_CONTEXT_URL}?${searchParams.toString()}`
  );

  if (!response || response.success === false) {
    throw new Error(response?.errors?.join(', ') || 'Failed to fetch preview context');
  }

  return response;
}

/**
 * Generate a simulated preview
 */
export async function generateSimulatedPreview(
  apiHost: string,
  request: SimulatePreviewRequest
): Promise<SimulatePreviewResponse> {
  const backendRequest: any = {};

  if (request.tag_html) {
    // For raw tag mode, backend expects campaign_id = 99999, creative_id = 0, and raw_tag
    backendRequest.campaign_id = 99999;
    backendRequest.creative_id = 0;
    backendRequest.raw_tag = request.tag_html;
  } else {
    if (request.campaign_id !== undefined && request.campaign_id !== null) {
      backendRequest.campaign_id = Number(request.campaign_id);
      if (Number.isNaN(backendRequest.campaign_id)) {
        throw new Error('campaign_id must be a valid number');
      }
    }
    if (request.creative_id !== undefined && request.creative_id !== null) {
      backendRequest.creative_id = Number(request.creative_id);
      if (Number.isNaN(backendRequest.creative_id)) {
        throw new Error('creative_id must be a valid number');
      }
    }
  }

  // Location: only send one parameter type (location_id takes priority over lat/lon)
  if (request.location_id !== undefined && request.location_id !== null) {
    backendRequest.location_id = Number(request.location_id);
    if (Number.isNaN(backendRequest.location_id)) {
      throw new Error('location_id must be a valid number');
    }
  } else if (request.lat !== undefined && request.lon !== undefined) {
    backendRequest.lat = Number(request.lat);
    backendRequest.lon = Number(request.lon);
    if (Number.isNaN(backendRequest.lat) || Number.isNaN(backendRequest.lon)) {
      throw new Error('lat and lon must be valid numbers');
    }
  }

  if (request.device_type !== undefined && request.device_type !== null) {
    backendRequest.device_type = request.device_type;
  }
  if (request.os !== undefined && request.os !== null) {
    backendRequest.os = request.os;
  }

  if (request.campaign_urls !== undefined && request.campaign_urls !== null) {
    backendRequest.campaign_urls = Array.isArray(request.campaign_urls)
      ? request.campaign_urls
      : [request.campaign_urls];
  }

  if (request.pixel_urls !== undefined && request.pixel_urls !== null) {
    backendRequest.pixel_urls = Array.isArray(request.pixel_urls)
      ? request.pixel_urls
      : [request.pixel_urls];
  }

  if (request.save_qa_preview !== undefined && request.save_qa_preview !== null) {
    backendRequest.save_qa_preview = request.save_qa_preview;
  }

  if (request.display_name !== undefined && request.display_name !== null && request.display_name !== '') {
    backendRequest.display_name = request.display_name;
  }

  if (request.selected_size !== undefined && request.selected_size !== null && request.selected_size !== '') {
    backendRequest.selected_size = request.selected_size;
  }

  const response = await postRequestJSON(
    apiHost,
    API_AD_PREVIEWS_SIMULATE_URL,
    backendRequest
  );

  if (!response || response.success === false) {
    throw new Error(response?.errors?.join(', ') || 'Failed to generate preview');
  }

  // Ensure response has mode field (backend may not always include it)
  if (!response.mode) {
    response.mode = response.preview?.campaign_id === 99999 ? 'qa' : 'simulated';
  }

  // Ensure metadata.pixels_found is an array (backend returns array of strings)
  if (response.metadata && !Array.isArray(response.metadata.pixels_found)) {
    response.metadata.pixels_found = [];
  }

  if (response.metadata && !Array.isArray(response.metadata.click_trackers)) {
    response.metadata.click_trackers = [];
  }

  // Ensure metadata.macros_replaced is an array
  if (response.metadata && !Array.isArray(response.metadata.macros_replaced)) {
    response.metadata.macros_replaced = [];
  }

  return response;
}

/**
 * Fetch campaigns for a given insertion order
 *
 * NOTE (campaigns flip, ACH-vrgd): this hits the SAME route ACH serves as the
 * campaign index, but it has no caller — the opted-in consumer is useAdGroups
 * (apiHostFor('campaigns')). Left on the passed-in Rails apiHost deliberately:
 * wire it through apiHostFor('campaigns') if it ever gains a consumer.
 */
export async function fetchCampaigns(
  apiHost: string,
  insertionOrderId: number
): Promise<any> {
  const response = await getRequestJSON(
    apiHost,
    `/admin/insertion_orders/${insertionOrderId}/campaigns`
  );

  if (!response) {
    throw new Error('Failed to fetch campaigns');
  }

  return response;
}

/**
 * Fetch targeted creatives for a campaign
 */
export async function fetchTargetedCreatives(
  apiHost: string,
  campaignId: number
): Promise<any> {
  const response = await getRequestJSON(
    apiHost,
    `/admin/campaigns/${campaignId}/targeted_creatives`
  );

  if (!response) {
    throw new Error('Failed to fetch targeted creatives');
  }

  return response;
}

/**
 * Fetch locations for a campaign
 */
export async function fetchCampaignLocations(
  apiHost: string,
  campaignId: number
): Promise<any> {
  const response = await getRequestJSON(
    apiHost,
    `/admin/campaigns/${campaignId}/locations/*`
  );

  if (!response) {
    throw new Error('Failed to fetch campaign locations');
  }

  return response;
}

/**
 * Fetch campaigns associated with a creative ID
 */
export async function fetchCampaignsByCreativeId(
  apiHost: string,
  creativeId: number
): Promise<any> {
  const response = await getRequestJSON(
    apiHost,
    `/admin/creatives/${creativeId}/campaigns`
  );

  if (!response) {
    throw new Error('Failed to fetch campaigns for creative');
  }

  return response;
}

// Export types for backward compatibility
export type PreviewSettings = {
  campaign_id?: number;
  creative_id?: number;
  location_id?: number;
  latitude?: number;
  longitude?: number;
  device_type?: DeviceType;
  os?: DeviceOS;
  context_type?: 'app' | 'site';
  bundle_id?: string;
  domain?: string;
  gdpr?: boolean;
  ccpa?: boolean;
  url0?: string;
  url1?: string;
  url2?: string;
  url3?: string;
  url4?: string;
  url5?: string;
  url6?: string;
  url7?: string;
  url8?: string;
  url9?: string;
  tag_html?: string;
};

// Compatibility type that maps SimulatePreviewResponse to old PreviewResponse format
export interface PreviewResponse {
  preview_url?: string;
  rendered_html: string;
  metadata: {
    creative_type: string;
    vendor?: string;
    macros_replaced: Array<{ macro: string; value: string }>;
    pixels_found: Array<{ url: string; type: string }>;
    click_trackers: string[];
    warnings: string[];
    settings_applied: Record<string, any>;
    iframe_url?: string;
    vast_url?: string;
  };
}

// Helper to convert SimulatePreviewResponse to PreviewResponse
export function convertToPreviewResponse(response: SimulatePreviewResponse): PreviewResponse {
  return {
    preview_url: response.preview_url,
    rendered_html: response.adm || '',
    metadata: {
      creative_type: response.metadata.creative_type,
      vendor: response.metadata.vendor,
      macros_replaced: response.metadata.macros_replaced,
      pixels_found: response.metadata.pixels_found.map(url => ({ url, type: 'impression' })),
      click_trackers: response.metadata.click_trackers,
      warnings: response.metadata.warnings,
      settings_applied: response.metadata.settings_applied,
      iframe_url: response.iframe_url,
      vast_url: response.vast_url,
    },
  };
}

/**
 * Check if campaign/creative is eligible for live preview
 */
export async function checkLivePreviewEligibility(
  apiHost: string,
  campaignId: number,
  creativeId: number
): Promise<LivePreviewEligibility> {
  const response = await postRequestJSON(
    apiHost,
    '/fakex/ad_previews/live_preview_eligibility',
    {
      campaign_id: campaignId,
      creative_id: creativeId,
    }
  );

  if (!response || response.success === false) {
    throw new Error(response?.errors?.join(', ') || 'Failed to check live preview eligibility');
  }

  return response.eligibility;
}

/**
 * Generate a live preview by calling Venice
 */
export async function generateLivePreview(
  apiHost: string,
  params: {
    campaign_id: number;
    creative_id: number;
    location_id?: number;
    fire_tracking?: boolean;
  }
): Promise<SimulatePreviewResponse> {
  const response = await postRequestJSON(
    apiHost,
    '/fakex/ad_previews/generate_live',
    params
  );

  if (!response || response.success === false) {
    const error = new Error(response?.errors?.join(', ') || 'Failed to generate live preview');
    (error as any).response = { data: response };
    throw error;
  }

  return response;
}

/**
 * Create a preview gallery with multiple creatives
 */
export async function createPreviewGallery(
  apiHost: string,
  request: CreateGalleryRequest
): Promise<GalleryResponse> {
  const response = await postRequestJSON(
    apiHost,
    API_PREVIEW_GALLERIES_URL,
    request
  );

  if (!response || response.errors) {
    const errors = response?.errors;
    const message = Array.isArray(errors) ? errors.join(', ') : errors || 'Failed to create gallery';
    throw new Error(message);
  }

  return response;
}

/**
 * Fetch a preview gallery by its public route
 */
export async function fetchPreviewGallery(
  apiHost: string,
  publicRoute: string
): Promise<GalleryResponse> {
  const url = API_PREVIEW_GALLERY_SHOW_URL.replace(':publicRoute', publicRoute);

  // Gallery is a public endpoint — fetch without auth credentials or JSON Accept header.
  // The Accept header triggers check_json_auth on the backend which returns 401 for
  // unauthenticated requests. Without it, the filter is bypassed and the endpoint
  // still returns JSON (it's the only format the controller renders).
  const response = await fetch(`${apiHost}${url}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `Failed to fetch gallery (${response.status})`);
  }

  const data = await response.json();

  if (!data || data.error) {
    throw new Error(data?.error || 'Failed to fetch gallery');
  }

  return data;
}

// Legacy function for backward compatibility
export const simulatePreview = async (
  apiHost: string,
  settings: PreviewSettings
): Promise<PreviewResponse> => {
  const request: SimulatePreviewRequest = {
    campaign_id: settings.campaign_id,
    creative_id: settings.creative_id,
    location_id: settings.location_id,
    lat: settings.latitude !== undefined ? Number(settings.latitude) : undefined,
    lon: settings.longitude !== undefined ? Number(settings.longitude) : undefined,
    device_type: settings.device_type,
    os: settings.os,
    tag_html: settings.tag_html,
  };

  const response = await generateSimulatedPreview(apiHost, request);
  return convertToPreviewResponse(response);
};

